import cx from "classnames";
import { FormProvider, useForm } from "react-hook-form";

import { useSelector } from "utils/redux";

import Header from "components/Header/Header.tsx";
import { useLocation } from "react-router-dom";
import KaboodleLeftNavigationPanel from "components/KaboodleLeftNavigationPanel";
import KaboodleRightSideBar from "components/Sidebar/KaboodleRightSideBar";
import LoadingIcon from "components/Loading/LoadingIcon.tsx";

import { KaboodleToaster } from "components/common";
import { useMemo } from "react";
import KaboodleStatusHeader from "./StatusHeader/KaboodleStatusHeader";

const KaboodleApp = ({
  children,
  classNames,
}: {
  children: React.ReactNode;
  classNames?: string;
}) => {
  const location = useLocation();
  const sideNavigationActive = useSelector(
    (state) => state.main.sideNavigationActive,
  );
  const isLoading = useSelector((state) => state.main.isLoading);

  const showStatusHeader = useMemo(() => {
    const path = location.pathname;
    // don't show fixed status header on overview pages
    if (path.includes("/overview")) return false;

    // don't show fixed status header on lien directory page
    if (path.includes("/directory")) return false;

    // don't show fixed status header on lien invoice page
    const invoiceRegex = /\/liens\/\d+\/invoice/;

    // don't show on MSATable page
    if (path === "/future-medical/msa") return false;

    if (invoiceRegex.test(path)) return false;
    // don't show fixed status header on lien query page
    const queryRegex = /\/liens\/\d+\/query/;
    if (queryRegex.test(path)) return false;
    const pathsWithHeader = ["liens", "future-medical"];
    return pathsWithHeader.some((headerPath) => path.includes(headerPath));
  }, [location.pathname]);

  // hide overflow on pages that don't need it, except for audit history and notes pages
  const usesOverflowHidden =
    (window.location.pathname.includes("future-medical/msa/") &&
      !window.location.pathname.includes("/audit-history") &&
      !window.location.pathname.includes("/notes")) ||
    window.location.pathname.includes("future-medical/amended-review/") ||
    window.location.pathname.includes("future-medical/ar-screening/") ||
    window.location.pathname.includes("future-medical/lcp/") ||
    window.location.pathname.includes("future-medical/mcp/") ||
    (window.location.pathname.includes("/liens/") &&
      !window.location.pathname.includes("/directory") &&
      !window.location.pathname.includes("/overview") &&
      !window.location.pathname.includes("/query") &&
      !window.location.pathname.includes("/audit-history") &&
      !window.location.pathname.includes("/notes")) ||
    window.location.pathname.includes("/document-management/") ||
    window.location.pathname.includes("/previews"); // PDF/Excel preview has its own scroll
  const showSubHeader = useSelector((state) => state.main.showSubHeader);

  const methods = useForm();

  const leftNav = useMemo(() => {
    return <KaboodleLeftNavigationPanel key={location.pathname} />;
  }, [location]);

  return (
    <FormProvider {...methods}>
      <div className={`main-wrapper ${showSubHeader}`}>
        <Header />

        <main
          className={cx("main-content", {
            active: sideNavigationActive,
            "w-status-header": showStatusHeader,
          })}
        >
          {/* All subpages go here*/}
          {leftNav}
          <div
            className={cx({
              "": showStatusHeader,
              // "header-container": !showStatusHeader,
            })}
          >
            {showStatusHeader && <KaboodleStatusHeader />}
            <div
              className={cx("content-wrapper", classNames, {
                "content-wrapper--loading": isLoading,
                "overflow-hidden": usesOverflowHidden,
              })}
            >
              {isLoading && <LoadingIcon />}
              <div className={cx({ "content-wrapper--hide": isLoading })}>
                {children}
                <KaboodleToaster.ToastContainer />
              </div>
            </div>
          </div>
          <KaboodleRightSideBar />
        </main>
      </div>
    </FormProvider>
  );
};

export default KaboodleApp;
